import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Leaf, ShieldCheck, Truck, Star, Quote, Utensils, Building2,
  TrendingUp, Users, ChefHat, Clock, IndianRupee, Phone, Sprout, Heart,
  CheckCircle2, Award, MapPin, Briefcase, BarChart3, Target, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials as mockTestimonials } from '../mock';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useImages } from '../hooks/useImages';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  'home-hero': 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85',
  'home-soil-farmer': 'https://images.unsplash.com/photo-1709552645252-f80c79dfb05d?crop=entropy&cs=srgb&fm=jpg&q=85',
  'home-cta-bg': 'https://images.unsplash.com/photo-1708513520903-f96f6cd9310d?crop=entropy&cs=srgb&fm=jpg&q=85',
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } })
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const Home = () => {
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [products, setProducts] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const siteImages = useImages();
  const img = (slug) => siteImages[slug] || DEFAULTS[slug] || '';
  const metricsRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/testimonials`).then(r => r.json()).then(d => { if (d.length > 0) setTestimonials(d); }).catch(() => {});
    fetch(`${API}/api/products`).then(r => r.json()).then(d => setProducts(d)).catch(() => {});
    fetch(`${API}/api/metrics`).then(r => r.json()).then(d => { if (d.length > 0) setMetrics(d); }).catch(() => {});
  }, []);

  // Default menu items (can be overridden by admin products)
  const menuItems = products.length > 0 ? products.slice(0, 6) : [
    { id: 'm1', name: 'Chicken Biryani', category: 'Best Seller', price: 199, description: '550g meal with raita, salan & salad', image: 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { id: 'm2', name: 'Veg Biryani', category: 'Popular', price: 149, description: '550g meal with raita, salan & salad', image: 'https://images.unsplash.com/photo-1752673508949-f4aeeaef75f0?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { id: 'm3', name: 'Mutton Biryani', category: 'Premium', price: 299, description: '550g meal with raita, salan & salad', image: 'https://images.unsplash.com/photo-1727404746799-253aa9a8ace6?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { id: 'm4', name: 'Egg Rice Bowl', category: 'Value', price: 129, description: 'Egg fried rice with curry & salad', image: 'https://images.unsplash.com/photo-1771796992969-05bb88a0516a?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { id: 'm5', name: 'Chicken Rice Bowl', category: 'Combo', price: 169, description: 'Chicken curry rice with sides', image: 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { id: 'm6', name: 'Veg Meal Combo', category: 'Healthy', price: 119, description: 'Dal, rice, roti, sabzi & salad', image: 'https://images.unsplash.com/photo-1752673508949-f4aeeaef75f0?crop=entropy&cs=srgb&fm=jpg&q=85' },
  ];

  const defaultMetrics = metrics.length > 0 ? metrics : [
    { value: '2,500+', label: 'Meals Served Daily', icon: Utensils },
    { value: '500+', label: 'Happy Customers', icon: Users },
    { value: '3', label: 'Active Kitchens', icon: ChefHat },
    { value: '15+', label: 'Corporate Clients', icon: Building2 },
  ];

  const processSteps = [
    { icon: Sprout, title: 'Sourcing', desc: 'Direct from farmers — chemical-free, fresh ingredients daily', color: '#5C7A3A' },
    { icon: ChefHat, title: 'Preparation', desc: 'Central hygienic kitchens with trained chefs', color: '#C4844A' },
    { icon: Truck, title: 'Delivery', desc: 'Workplace & last-mile distribution, on time', color: '#5C7A3A' },
    { icon: IndianRupee, title: 'Revenue', desc: 'Daily sales + subscriptions + corporate contracts', color: '#C4844A' },
  ];

  const whyChoose = [
    { icon: Clock, text: 'Freshly prepared daily' },
    { icon: Utensils, text: 'Balanced 550g meals' },
    { icon: IndianRupee, text: 'Affordable pricing' },
    { icon: Star, text: 'Consistent taste' },
    { icon: ShieldCheck, text: 'Hygienic kitchens' },
    { icon: Briefcase, text: 'Built for working professionals' },
  ];

  const mealPlans = [
    { name: 'Weekly Plan', price: '799', unit: '/week', desc: '5 meals (Mon–Fri)', tag: 'Popular', items: ['Choice of Biryani/Rice', 'Raita + Salan + Salad', 'Free delivery'] },
    { name: 'Monthly Plan', price: '2,999', unit: '/month', desc: '22 meals', tag: 'Best Value', items: ['Rotating daily menu', 'All sides included', 'Priority delivery', 'Pause anytime'] },
    { name: 'Corporate Pack', price: 'Custom', unit: '', desc: 'For teams of 10+', tag: 'B2B', items: ['Bulk pricing', 'Multi-location delivery', 'Dedicated account manager', 'Invoice billing'] },
  ];

  return (
    <div className="min-h-screen overflow-hidden" data-testid="home-page">
      <SEO />

      {/* ===== 1. HERO SECTION ===== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0 z-0" style={{ y: heroY }}>
          <img src={img('home-hero')} alt="Fresh meals" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#2C1810]/70 to-[#2C1810]/40" />
        </motion.div>
        <motion.div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-20" style={{ opacity: heroOpacity }}>
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl">
            <motion.span variants={fadeUp} className="inline-block text-xs tracking-[0.25em] uppercase text-[#D4A574] font-semibold mb-4 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full">
              Farm-to-Workplace Food
            </motion.span>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
              Fresh, Healthy Meals<br />
              <span className="text-[#D4A574]">Delivered Daily.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-white/80 mb-8 leading-relaxed max-w-lg">
              Chemical-free sourcing. Hygienic kitchens. Affordable meals designed for working professionals.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-3">
              <Link to="/products">
              <Button data-testid="hero-order-btn" className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-7 py-3 text-sm rounded-lg cursor-pointer shadow-lg shadow-[#5C7A3A]/30">
                Order Now <ArrowRight size={16} className="ml-2" />
              </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-partner-btn" variant="outline" className="border-white/40 text-white hover:bg-white/10 font-semibold px-7 py-3 text-sm rounded-lg cursor-pointer backdrop-blur-sm">
                  Partner With Us
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-invest-btn" variant="outline" className="border-[#D4A574]/50 text-[#D4A574] hover:bg-[#D4A574]/10 font-semibold px-7 py-3 text-sm rounded-lg cursor-pointer backdrop-blur-sm">
                  Invest in Nistala's
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== 2. WHAT WE OFFER ===== */}
      <section className="py-12 bg-white -mt-1" data-testid="offerings-section">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>What We Offer</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-sm text-[#7a6555]">Wholesome meals crafted for busy lives</motion.p>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Utensils, title: 'Biryani Combos', desc: '550g meals with raita, salan & salad', color: '#C4844A' },
              { icon: ChefHat, title: 'Rice Bowls & Daily Meals', desc: 'Balanced nutrition, fresh ingredients', color: '#5C7A3A' },
              { icon: Building2, title: 'Corporate Meal Services', desc: 'Bulk orders for teams & offices', color: '#C4844A' },
              { icon: Users, title: 'Bulk Orders & Catering', desc: 'Events, parties & special occasions', color: '#5C7A3A' },
            ].map((item, idx) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
                <Card className="h-full border border-[#E8DFD3] bg-[#FDF8F0] hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-400 hover:-translate-y-1">
                  <CardContent className="p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: `${item.color}15` }}>
                      <item.icon size={22} style={{ color: item.color }} />
                    </div>
                    <h3 className="text-sm font-bold text-[#2C1810] mb-1">{item.title}</h3>
                    <p className="text-xs text-[#7a6555] leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-6 text-center">
            <p className="text-xs text-[#C4844A] font-semibold bg-[#C4844A]/8 inline-block px-5 py-2 rounded-full">
              Every meal includes: Raita + Salan + Salad
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== 3. HOW IT WORKS (Soil to Soul) ===== */}
      <section className="py-12" style={{ background: '#FDF8F0' }} data-testid="process-section">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>From Soil to Soul</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-sm text-[#7a6555]">Our 4-step process ensures quality at every stage</motion.p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((step, idx) => (
              <motion.div key={step.title} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="relative">
                <div className="bg-white rounded-xl p-5 border border-[#E8DFD3] shadow-sm h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ background: step.color }}>
                      {idx + 1}
                    </div>
                    <step.icon size={20} style={{ color: step.color }} />
                  </div>
                  <h3 className="text-base font-bold text-[#2C1810] mb-1">{step.title}</h3>
                  <p className="text-xs text-[#7a6555] leading-relaxed">{step.desc}</p>
                </div>
                {idx < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 text-[#D4A574]"><ArrowRight size={18} /></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. WHY CHOOSE US ===== */}
      <section className="py-12 bg-white" data-testid="why-choose-section">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Why Customers Trust Nistala's</motion.h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {whyChoose.map((item, idx) => (
              <motion.div key={item.text} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.06 }}
                className="flex items-center gap-3 bg-[#FDF8F0] rounded-xl p-4 border border-[#E8DFD3]">
                <div className="w-9 h-9 rounded-lg bg-[#5C7A3A]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={18} className="text-[#5C7A3A]" />
                </div>
                <p className="text-sm font-medium text-[#2C1810]">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. SIGNATURE MENU ===== */}
      <section className="py-12" style={{ background: '#FDF8F0' }} data-testid="menu-section">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Our Signature Menu</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-xs text-[#7a6555]">Prices inclusive of GST</motion.p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {menuItems.map((item, idx) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.06 }}>
                <Card className="group h-full border border-[#E8DFD3] bg-white overflow-hidden hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-400 hover:-translate-y-1" data-testid={`menu-item-${item.id}`}>
                  <div className="relative h-36 overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-2 left-2 bg-[#2C1810]/80 text-white text-[10px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm">{item.category}</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-bold text-[#2C1810] mb-1">{item.name}</h3>
                    <p className="text-xs text-[#7a6555] mb-2">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-[#5C7A3A]">₹{item.price}</span>
                      <Button size="sm" className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-xs px-3 py-1 h-auto cursor-pointer">Order</Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. DAILY MEAL PLANS ===== */}
      <section className="py-12 bg-white" data-testid="plans-section">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Smart Meal Plans for Busy Lives</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-sm text-[#7a6555]">Starting from ₹119 per meal</motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {mealPlans.map((plan, idx) => (
              <motion.div key={plan.name} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <Card className={`h-full border ${idx === 1 ? 'border-[#5C7A3A] shadow-lg shadow-[#5C7A3A]/10 relative' : 'border-[#E8DFD3] shadow-sm'} bg-white`}>
                  {idx === 1 && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#5C7A3A] text-white text-[10px] font-bold px-4 py-1 rounded-full">BEST VALUE</div>}
                  <CardContent className="p-6">
                    <span className="text-[10px] font-bold tracking-wider uppercase text-[#C4844A] bg-[#C4844A]/8 px-3 py-1 rounded-full">{plan.tag}</span>
                    <h3 className="text-xl font-bold text-[#2C1810] mt-3 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.name}</h3>
                    <p className="text-xs text-[#7a6555] mb-3">{plan.desc}</p>
                    <div className="flex items-baseline gap-1 mb-4">
                      {plan.price !== 'Custom' && <span className="text-xs text-[#7a6555]">₹</span>}
                      <span className="text-3xl font-bold text-[#2C1810]">{plan.price}</span>
                      <span className="text-xs text-[#7a6555]">{plan.unit}</span>
                    </div>
                    <ul className="space-y-2 mb-5">
                      {plan.items.map(item => (
                        <li key={item} className="flex items-center gap-2 text-xs text-[#7a6555]">
                          <CheckCircle2 size={14} className="text-[#5C7A3A] flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                    <Button className={`w-full cursor-pointer text-sm ${idx === 1 ? 'bg-[#5C7A3A] hover:bg-[#4a6530] text-white' : 'bg-[#FDF8F0] hover:bg-[#E8DFD3] text-[#2C1810]'}`}>
                      {plan.price === 'Custom' ? 'Get Quote' : 'Subscribe Now'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6.5 JOIN NISTALA'S CIRCLE (Membership CTA) ===== */}
      <section className="py-12 bg-gradient-to-br from-[#2C1810] to-[#1a0e08] relative overflow-hidden" data-testid="membership-section">
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 20% 50%, #D4A574 0%, transparent 50%)' }} />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -25 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 bg-[#D4A574]/15 border border-[#D4A574]/25 px-3 py-1 rounded-full mb-4">
                <Crown size={12} className="text-[#D4A574]" />
                <span className="text-[10px] tracking-[0.15em] uppercase text-[#D4A574] font-semibold">Loyalty Program</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Join the <span className="text-[#D4A574]">Nistala's Circle</span>
              </h2>
              <p className="text-sm text-white/70 mb-5 max-w-md">
                More Benefits. More Value. More Belonging. Unlock up to 25% off, free delivery, monthly curated boxes, and exclusive access.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/membership">
                  <Button className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-6 py-3 cursor-pointer" data-testid="home-membership-btn">
                    View Membership Plans <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/membership">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 cursor-pointer" data-testid="home-join-btn">
                    Join Now — It's Free
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 25 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="grid grid-cols-2 gap-3">
              {[
                { label: 'Free Tier', value: '10% OFF', sub: 'First 3 orders' },
                { label: 'Silver', value: '12% OFF', sub: '₹999/year' },
                { label: 'Gold', value: '18% OFF', sub: '₹2,999/year' },
                { label: 'Platinum', value: '25% OFF', sub: 'Lifetime' }
              ].map((t, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center backdrop-blur-sm hover:bg-white/10 transition-colors">
                  <p className="text-[10px] text-[#D4A574] font-semibold uppercase tracking-wide mb-1">{t.label}</p>
                  <p className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{t.value}</p>
                  <p className="text-[10px] text-white/50 mt-0.5">{t.sub}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== 7. TRUST & PROOF (Metrics Strip) ===== */}
      <section className="py-10 bg-[#2C1810]" ref={metricsRef} data-testid="metrics-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {defaultMetrics.map((m, idx) => {
              const Icon = m.icon || BarChart3;
              return (
                <motion.div key={m.label || idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="text-center">
                  <Icon size={24} className="mx-auto mb-2 text-[#D4A574]" />
                  <p className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{m.value}</p>
                  <p className="text-xs text-white/60 mt-1">{m.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 8. CORPORATE SECTION ===== */}
      <section className="py-12 bg-white" data-testid="corporate-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -25 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-xs tracking-[0.2em] uppercase text-[#C4844A] font-semibold">For Businesses</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2C1810] mt-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Fuel Your Team with<br /><span className="text-[#5C7A3A]">Healthy Meals</span>
              </h2>
              <div className="space-y-3 mb-6">
                {[
                  { icon: ChefHat, text: 'Custom meal plans tailored to your team' },
                  { icon: Truck, text: 'Bulk delivery to multiple office locations' },
                  { icon: IndianRupee, text: 'Affordable corporate pricing' },
                  { icon: MapPin, text: 'Multi-location scalability' },
                ].map(item => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#5C7A3A]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={16} className="text-[#5C7A3A]" />
                    </div>
                    <p className="text-sm text-[#7a6555]">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="bg-[#C4844A] hover:bg-[#A96E3A] text-white font-semibold px-6 cursor-pointer">
                  Get Corporate Proposal <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 25 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <img src="https://images.unsplash.com/photo-1666479258732-5ea17469b610?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Kitchen operations" className="rounded-2xl shadow-xl w-full h-72 object-cover" />
              <div className="absolute -bottom-4 -left-4 bg-[#5C7A3A] text-white rounded-xl p-4 shadow-lg">
                <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>15+</p>
                <p className="text-[10px] text-white/80">Corporate Clients</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== 9. INVESTOR SECTION ===== */}
      <section className="relative py-14 overflow-hidden bg-[#2C1810]" data-testid="investor-section">
        <div className="absolute inset-0 opacity-10">
          <img src={img('home-soil-farmer')} alt="Farm" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-[#D4A574] font-semibold">For Investors</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Building India's Farm-to-Workplace <span className="text-[#D4A574]">Food Brand</span>
              </h2>
              <p className="text-sm text-white/70 mb-6 leading-relaxed">
                A scalable, mission-driven food business addressing the ₹5,000 Cr healthy workplace meals market.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon: TrendingUp, text: 'Growing market demand' },
                  { icon: Target, text: 'Scalable kitchen model' },
                  { icon: Heart, text: 'Repeat customer economics' },
                  { icon: Award, text: 'Pan-India expansion ready' },
                ].map(item => (
                  <div key={item.text} className="flex items-center gap-2">
                    <item.icon size={16} className="text-[#D4A574] flex-shrink-0" />
                    <p className="text-xs text-white/80">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button className="bg-[#D4A574] hover:bg-[#C4944A] text-[#2C1810] font-semibold px-6 cursor-pointer" data-testid="invest-deck-btn">
                    View Investment Deck
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 cursor-pointer" data-testid="invest-call-btn">
                    Schedule a Call
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: '₹5,000 Cr', label: 'Market Size' },
                  { val: '40%', label: 'Gross Margins' },
                  { val: '3x', label: 'YoY Growth' },
                  { val: '85%', label: 'Retention Rate' },
                ].map(item => (
                  <div key={item.label} className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                    <p className="text-xl font-bold text-[#D4A574]" style={{ fontFamily: 'Playfair Display, serif' }}>{item.val}</p>
                    <p className="text-[10px] text-white/60 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 10. TESTIMONIALS ===== */}
      <section className="py-12" style={{ background: '#FDF8F0' }} data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>What People Say</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t, idx) => (
              <motion.div key={t.id || idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <Card className="h-full bg-white border border-[#E8DFD3] shadow-sm">
                  <CardContent className="p-5">
                    <Quote size={20} className="text-[#D4A574]/40 mb-3" />
                    <p className="text-sm text-[#7a6555] leading-relaxed mb-4 italic">"{t.content}"</p>
                    <div className="flex items-center gap-3">
                      <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-[#E8DFD3]" />
                      <div>
                        <p className="text-sm font-bold text-[#2C1810]">{t.name}</p>
                        <p className="text-xs text-[#7a6555]">{t.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 11. BRAND STORY (Short) ===== */}
      <section className="py-10 bg-white" data-testid="brand-story-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-4 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16" />
              <Leaf size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16" />
            </motion.div>
            <motion.p variants={fadeUp} custom={1} className="text-base text-[#7a6555] leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
              "Nistala's reconnects food with its roots — delivering fresh, honest meals from farms to working professionals through a scalable ecosystem."
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="mt-3">
              <Link to="/about" className="text-sm font-semibold text-[#5C7A3A] hover:text-[#4a6530] inline-flex items-center gap-1 transition-colors">
                Read Our Story <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== 12. FINAL CTA ===== */}
      <section className="py-14 bg-[#2C1810]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Eat Better. <span className="text-[#D4A574]">Live Better.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="text-sm text-white/60 mb-6 max-w-md mx-auto">
            Join thousands of professionals who trust Nistala's for their daily meals.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="flex flex-wrap justify-center gap-3">
            <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-8 py-3 cursor-pointer shadow-lg shadow-[#5C7A3A]/30" data-testid="cta-order-btn">
              Order Now <Utensils size={16} className="ml-2" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-3 cursor-pointer" data-testid="cta-partner-btn">
                Partner With Us
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#D4A574]/40 text-[#D4A574] hover:bg-[#D4A574]/10 font-semibold px-8 py-3 cursor-pointer" data-testid="cta-invest-btn">
                Invest
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Home;
